import { InfoHint } from '@eo/shared';

import { formatBillHours, formatUsd } from './helpers';
import { HoursStepper } from './HoursStepper';
import type { ExcessSummaryProps } from './types';

export function ExcessSummary({ totals, writeoffHours, onWriteoff }: ExcessSummaryProps) {
  const lines = [
    { label: 'Total variable hours', value: formatBillHours(totals.variable), strong: true },
    { label: 'Variable hours included in plan', value: `−${formatBillHours(totals.included)}` },
    {
      label: 'Courtesy write-off',
      value: totals.writeoff > 0 ? `−${formatBillHours(totals.writeoff)}` : formatBillHours(0),
    },
    { label: 'Total excess hours', value: formatBillHours(totals.excess), strong: true },
    { label: 'Rate per hour', value: formatUsd(totals.rate) },
  ];

  return (
    <section data-testid="excess-summary" className="flex flex-col text-sm">
      <div className="flex items-center justify-between gap-4 border-b border-border-faint py-3">
        <label htmlFor="excess-writeoff" className="flex items-center gap-1 text-text-secondary">
          One-time courtesy hours write-off
          <InfoHint term="Courtesy write-off">
            Hours taken off this month’s excess as a one-time courtesy. They lower the excess hours
            and never reach ClickUp.
          </InfoHint>
        </label>
        <HoursStepper
          id="excess-writeoff"
          label="One-time courtesy hours write-off"
          value={writeoffHours}
          min={0}
          onCommit={onWriteoff}
          className="w-28"
        />
      </div>
      <dl>
        {lines.map((line) => (
          <div
            key={line.label}
            className="flex items-baseline justify-between gap-4 border-b border-border-faint py-2.5"
          >
            <dt className={line.strong ? 'text-text-primary' : 'text-text-secondary'}>
              {line.label}
            </dt>
            <dd
              className={`tabular-nums ${line.strong ? 'text-text-primary' : 'text-text-secondary'}`}
            >
              {line.value}
            </dd>
          </div>
        ))}
        <div className="mt-2 flex items-center justify-between gap-4 border-t border-text-primary py-4">
          <dt className="text-base text-text-primary">Total excess hours billable</dt>
          <dd data-testid="excess-amount" className="text-2xl tabular-nums text-text-primary">
            {formatUsd(totals.amount)}
          </dd>
        </div>
      </dl>
    </section>
  );
}
